/**
 * Validation Report Viewer Component
 * 
 * Displays validation reports with color-coded status badges,
 * grouped checks by severity, and expandable details.
 * 
 * Implements Issue #234 requirements for validation report display.
 */

import { ValidationReport, ValidationCheck, ValidationSeverity } from '../../types/validation.js';

/**
 * Render a validation report
 * 
 * @param report - Validation report to display
 * @param container - Container element
 * @param options - Display options
 */
export function renderValidationReport(
  report: ValidationReport,
  container: HTMLElement,
  options: { collapsible?: boolean; defaultCollapsed?: boolean } = {}
): void {
  // Clear container
  container.innerHTML = '';
  container.className = 'validation-report';
  
  // Create header
  const header = document.createElement('div');
  header.className = 'validation-report__header';
  
  const titleRow = document.createElement('div');
  titleRow.className = 'validation-report__title-row';
  
  const title = document.createElement('h3');
  title.className = 'validation-report__title';
  title.textContent = 'Validation Report';
  titleRow.appendChild(title);
  
  // Overall status badge
  const statusBadge = createStatusBadge(report.overallStatus);
  titleRow.appendChild(statusBadge);
  
  header.appendChild(titleRow);
  
  // Summary stats
  const summary = document.createElement('div');
  summary.className = 'validation-report__summary';
  summary.innerHTML = `
    <span class="validation-stat validation-stat--passed">
      ✓ ${report.summary.passed} Passed
    </span>
    <span class="validation-stat validation-stat--failed">
      ✗ ${report.summary.failed} Failed
    </span>
    <span class="validation-stat validation-stat--warnings">
      ⚠ ${report.summary.warnings} Warnings
    </span>
  `;
  header.appendChild(summary);
  
  // Quality score if available
  if (report.qualityScore !== null && report.qualityScore !== undefined) {
    const qualityScore = document.createElement('div');
    qualityScore.className = 'validation-report__quality-score';
    qualityScore.innerHTML = `
      <span class="quality-score-label">Quality Score:</span>
      <span class="quality-score-value quality-score-value--${getQualityScoreClass(report.qualityScore)}">
        ${report.qualityScore}/100
      </span>
    `;
    header.appendChild(qualityScore);
  }
  
  // Timestamp
  const timestamp = document.createElement('div');
  timestamp.className = 'validation-report__timestamp';
  timestamp.textContent = `Last validated: ${formatTimestamp(report.timestamp)}`;
  header.appendChild(timestamp);
  
  container.appendChild(header);
  
  // Checks section
  const checksSection = document.createElement('div');
  checksSection.className = 'validation-report__checks';
  
  if (options.collapsible) {
    checksSection.classList.add('validation-report__checks--collapsible');
    if (options.defaultCollapsed) {
      checksSection.classList.add('validation-report__checks--collapsed');
    }
    
    // Add toggle button
    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'validation-report__toggle';
    toggleBtn.textContent = options.defaultCollapsed ? 'Show Details' : 'Hide Details';
    toggleBtn.addEventListener('click', () => {
      checksSection.classList.toggle('validation-report__checks--collapsed');
      toggleBtn.textContent = checksSection.classList.contains('validation-report__checks--collapsed')
        ? 'Show Details'
        : 'Hide Details';
    });
    header.appendChild(toggleBtn);
  }
  
  // Group checks by severity
  const groupedChecks = groupChecksBySeverity(report.checks);
  
  // Render each severity group
  const severityOrder: ValidationSeverity[] = ['critical', 'high', 'medium', 'low'];
  severityOrder.forEach(severity => {
    if (groupedChecks[severity] && groupedChecks[severity].length > 0) {
      const group = renderSeverityGroup(severity, groupedChecks[severity]);
      checksSection.appendChild(group);
    }
  });
  
  container.appendChild(checksSection);
}

/**
 * Create status badge for overall status
 */
function createStatusBadge(status: string): HTMLElement {
  const badge = document.createElement('span');
  badge.className = `validation-status-badge validation-status-badge--${status}`;
  
  let icon = '';
  let text = '';
  
  switch (status) {
    case 'passed':
      icon = '✓';
      text = 'Passed';
      break;
    case 'failed':
      icon = '✗';
      text = 'Failed';
      break;
    case 'warning':
      icon = '⚠';
      text = 'Warning';
      break;
  }
  
  badge.innerHTML = `${icon} ${text}`;
  return badge;
}

/**
 * Group checks by severity
 */
function groupChecksBySeverity(checks: ValidationCheck[]): Record<ValidationSeverity, ValidationCheck[]> {
  const grouped: Record<ValidationSeverity, ValidationCheck[]> = {
    critical: [],
    high: [],
    medium: [],
    low: []
  };
  
  checks.forEach(check => {
    grouped[check.severity].push(check);
  });
  
  return grouped;
}

/**
 * Render a severity group
 */
function renderSeverityGroup(severity: ValidationSeverity, checks: ValidationCheck[]): HTMLElement {
  const group = document.createElement('div');
  group.className = `validation-severity-group validation-severity-group--${severity}`;
  
  const header = document.createElement('div');
  header.className = 'validation-severity-group__header';
  
  const severityLabel = document.createElement('h4');
  severityLabel.className = 'validation-severity-group__title';
  severityLabel.textContent = `${severity.charAt(0).toUpperCase() + severity.slice(1)} (${checks.length})`;
  header.appendChild(severityLabel);
  
  group.appendChild(header);
  
  const checksList = document.createElement('div');
  checksList.className = 'validation-checks-list';
  
  checks.forEach(check => {
    const checkElement = renderCheck(check);
    checksList.appendChild(checkElement);
  });
  
  group.appendChild(checksList);
  
  return group;
}

/**
 * Render a single validation check
 */
function renderCheck(check: ValidationCheck): HTMLElement {
  const checkElement = document.createElement('div');
  checkElement.className = `validation-check validation-check--${check.status}`;
  
  const checkHeader = document.createElement('div');
  checkHeader.className = 'validation-check__header';
  
  // Status icon
  const statusIcon = document.createElement('span');
  statusIcon.className = 'validation-check__status-icon';
  statusIcon.textContent = check.status === 'passed' ? '✓' : 
                           check.status === 'failed' ? '✗' : '⚠';
  checkHeader.appendChild(statusIcon);
  
  // Check name
  const name = document.createElement('span');
  name.className = 'validation-check__name';
  name.textContent = check.name;
  checkHeader.appendChild(name);
  
  // Tool badge
  const toolBadge = document.createElement('span');
  toolBadge.className = 'validation-check__tool';
  toolBadge.textContent = check.tool;
  checkHeader.appendChild(toolBadge);
  
  checkElement.appendChild(checkHeader);
  
  // Message
  const message = document.createElement('div');
  message.className = 'validation-check__message';
  message.textContent = check.message;
  checkElement.appendChild(message);
  
  // Details (if available)
  if (check.details) {
    const details = document.createElement('div');
    details.className = 'validation-check__details';
    details.innerHTML = `<strong>Details:</strong> ${escapeHtml(check.details)}`;
    checkElement.appendChild(details);
  }
  
  // Suggested fix (if available)
  if (check.suggestedFix) {
    const fix = document.createElement('div');
    fix.className = 'validation-check__suggested-fix';
    fix.innerHTML = `<strong>Suggested Fix:</strong> ${escapeHtml(check.suggestedFix)}`;
    checkElement.appendChild(fix);
  }
  
  return checkElement;
}

/**
 * Get quality score CSS class
 */
function getQualityScoreClass(score: number): string {
  if (score >= 80) return 'good';
  if (score >= 60) return 'medium';
  return 'low';
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  
  return date.toLocaleString();
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create a compact validation status indicator
 */
export function createValidationStatusIndicator(
  report: ValidationReport | null,
  needsValidation: boolean = false
): HTMLElement {
  const indicator = document.createElement('div');
  indicator.className = 'validation-status-indicator';
  
  if (needsValidation) {
    indicator.classList.add('validation-status-indicator--needs-validation');
    indicator.innerHTML = `
      <span class="validation-status-indicator__icon">⚠</span>
      <span class="validation-status-indicator__text">Validation Needed</span>
    `;
    return indicator;
  }
  
  if (!report) {
    indicator.classList.add('validation-status-indicator--none');
    indicator.innerHTML = `
      <span class="validation-status-indicator__text">Not Validated</span>
    `;
    return indicator;
  }
  
  indicator.classList.add(`validation-status-indicator--${report.overallStatus}`);
  
  let icon = '';
  let text = '';
  
  switch (report.overallStatus) {
    case 'passed':
      icon = '✓';
      text = 'Valid';
      break;
    case 'failed':
      icon = '✗';
      text = 'Failed';
      break;
    case 'warning':
      icon = '⚠';
      text = 'Warnings';
      break;
  }
  
  indicator.innerHTML = `
    <span class="validation-status-indicator__icon">${icon}</span>
    <span class="validation-status-indicator__text">${text}</span>
    <span class="validation-status-indicator__timestamp">${formatTimestamp(report.timestamp)}</span>
  `;
  
  return indicator;
}
